import React, { useEffect, useMemo, useState } from 'react';

import { combineClassNames } from '@utils/combineClassNames';
import { Message } from '../../message';

export type TextInputOwnProps = JSX.IntrinsicElements['input'] & {
  name?: string;
  type?: 'text' | 'email' | 'password';
  className?: string;
  warning?: string | boolean;
  error?: string | boolean;
  mask?: boolean;
  autoGrow?: number;
  maxLength?: number;
  /** When set with `maxLength`, omit the in-field `n/max` counter (e.g. dark free-trial glass). */
  hideMaxLengthCounter?: boolean;
  ref?: React.MutableRefObject<HTMLInputElement | undefined>,
};

export const TextInput = React.forwardRef<HTMLInputElement | undefined, TextInputOwnProps>(({
  type = 'text',
  value,
  className,
  error,
  warning,
  mask,
  autoGrow,
  maxLength,
  hideMaxLengthCounter,
  id,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  ...otherProps
}, ref) => {
  const hasValidationError = !warning && !!error;
  const errorMessageId = id && typeof error === 'string' ? `${id}-error` : undefined;

  const fullClassName = combineClassNames('Form__control', className, {
    'Form__control--invalid': hasValidationError,
    'Form__control--warning': !!warning,
    'fs-mask': mask,
  });

  const [width, setWidth] = useState('');
  const canvas = useMemo(() => {
    return autoGrow ? document.createElement('canvas') : undefined;
  }, [autoGrow]);

  useEffect(() => {
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.font = `${autoGrow}px Inter`;
        const metrics = ctx.measureText(`${value}`);
        setWidth(metrics.width > 0 ? `${Math.ceil(metrics.width)}px` : 'auto');
      }
    }
  }, [canvas, value, ref, autoGrow]);

  const inputElement = (
    <input
      ref={ref as React.ForwardedRef<HTMLInputElement>}
      // Dynamic Tailwind classes like `tw-w-[${width}px]` are not generated at
      // build time (and become `tw-w-[px]` when width is empty). Use inline style.
      style={autoGrow ? { width: width || undefined } : undefined}
      className={fullClassName}
      type={type}
      value={value}
      maxLength={maxLength}
      id={id}
      aria-invalid={ariaInvalid ?? (hasValidationError ? true : undefined)}
      aria-describedby={ariaDescribedBy ?? errorMessageId}
      {...otherProps}
    />
  );

  if (maxLength != null && !hideMaxLengthCounter) {
    const valueLength = typeof value === 'string' ? value.length : 0;
    return (
      <>
        <div
          className={combineClassNames('Form__TextInput Form__TextInput--max-length', {
            'Form__TextInput--invalid': !warning && !!error,
          })}
        >
          {inputElement}
          <small>
            {valueLength}/{maxLength}
          </small>
        </div>
        <Message error={error} warning={warning} id={errorMessageId} />
      </>
    );
  }
  return (
    <>
      {inputElement}
      <Message error={error} warning={warning} id={errorMessageId} />
    </>
  );
});
